import type { ContentTag, ContentTagCategory } from '@getaccept/dsr-shared';
import { apolloClientInternal } from '@getaccept/lib-shared/src/internal/apollo-client-internal';
import { GraphQLFetchPolicy } from '@getaccept/lib-shared/src/graphql/graphql-fetch-policy';
import type { TagResourceCount } from '../../../settings/dealroom-tags/types/tag-resource-count';
import { ContentTagMutations } from '../graphql/content-tag.mutations';
import { ContentTagQueries } from '../graphql/content-tag.queries';
import type { UpdateTagDto } from '../types/update-tag-dto';

export class ContentTagService {
  static async load(): Promise<ContentTagCategory[]> {
    const {
      data: {
        dealroomResourceTagCategories: { categories },
      },
    } = await apolloClientInternal.query({
      query: ContentTagQueries.load,
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return categories;
  }

  static async loadTagResourceCount(): Promise<TagResourceCount[]> {
    const {
      data: {
        dsrResourceTagsCount: { tagsCount },
      },
    } = await apolloClientInternal.query({
      query: ContentTagQueries.loadTagResourceCount,
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return tagsCount;
  }

  static async createCategory(name: string): Promise<ContentTagCategory> {
    const {
      data: { createDealroomResourceTagCategory },
    } = await apolloClientInternal.mutate({
      mutation: ContentTagMutations.createCategory,
      variables: { payload: { name } },
    });
    return createDealroomResourceTagCategory;
  }

  static async deleteCategory(categoryId: string): Promise<void> {
    await apolloClientInternal.mutate({
      mutation: ContentTagMutations.deleteCategory,
      variables: { categoryId },
    });
  }

  static async updateCategory(name: string, categoryId: string): Promise<void> {
    await apolloClientInternal.mutate({
      mutation: ContentTagMutations.updateCategory,
      variables: { payload: { name }, categoryId },
    });
  }

  static async createTag(name: string, categoryId: string): Promise<ContentTag> {
    const {
      data: { createDealroomResourceTag },
    } = await apolloClientInternal.mutate({
      mutation: ContentTagMutations.createTag,
      variables: { payload: { name }, categoryId },
    });
    return createDealroomResourceTag;
  }

  static async deleteTag(tagId: string, categoryId: string): Promise<void> {
    await apolloClientInternal.mutate({
      mutation: ContentTagMutations.deleteTag,
      variables: { tagId, categoryId },
    });
  }

  static async updateTag(payload: UpdateTagDto, tagId: string, categoryId: string): Promise<void> {
    await apolloClientInternal.mutate({
      mutation: ContentTagMutations.updateTag,
      variables: { payload, tagId, categoryId },
    });
  }
}
